/* eslint-disable array-callback-return */
import React, {
  lazy,
  useEffect,
  useMemo,
  useState
} from "react";
import {
  useNavigate,
  useLocation
} from "react-router-dom";
import {
  IListMenuNavigationProps,
  INavigationProps
} from "./types";

import "./styles.scss";

import ArticleOutlined        from "@mui/icons-material/ArticleOutlined";
import CheckCircle            from "@mui/icons-material/CheckCircle";
import RadioButtonChecked     from "@mui/icons-material/RadioButtonChecked";
import RadioButtonUnchecked   from "@mui/icons-material/RadioButtonUnchecked";
import PlayCircleOutline      from "@mui/icons-material/PlayCircleOutline";
import SchoolOutlined         from "@mui/icons-material/SchoolOutlined";
import EmojiObjectsOutlined   from "@mui/icons-material/EmojiObjectsOutlined";
import CloudUploadOutlined    from "@mui/icons-material/CloudUploadOutlined";
import WbIncandescentOutlined from "@mui/icons-material/WbIncandescentOutlined";
import ThumbUpOutlined        from "@mui/icons-material/ThumbUpOutlined";
import BallotOutlined         from "@mui/icons-material/BallotOutlined";
import HelpOutline            from "@mui/icons-material/HelpOutline";
import BookOutlined           from "@mui/icons-material/BookOutlined";
import HomeOutlined           from "@mui/icons-material/HomeOutlined";
import ExpandMore             from "@mui/icons-material/ExpandMore";
import axios                  from "axios";
import useBookProgress        from "context/BookProgressContext/BookProgress.hook";
import Loading                from "pages/components/Loading";
import { onGoEcossistema }    from "../Header";
import useDataContext         from "context/data.hook";

const List              = lazy(() => import("@mui/material/List"));
const ListItem          = lazy(() => import("@mui/material/ListItem"));
const ListItemButton    = lazy(() => import("@mui/material/ListItemButton"));
const ListItemIcon      = lazy(() => import("@mui/material/ListItemIcon"));
const ListItemText      = lazy(() => import("@mui/material/ListItemText"));
const Toolbar           = lazy(() => import("@mui/material/Toolbar"));
const Box               = lazy(() => import("@mui/material/Box"));
const LinearProgress    = lazy(() => import("@mui/material/LinearProgress"));
const Typography        = lazy(() => import("@mui/material/Typography"));
const Divider           = lazy(() => import("@mui/material/Divider"));
const Button            = lazy(() => import("@mui/material/Button"));
const Accordion         = lazy(() => import("@mui/material/Accordion"));
const AccordionSummary  = lazy(() => import("@mui/material/AccordionSummary"));
const AccordionDetails  = lazy(() => import("@mui/material/AccordionDetails"));
const DialogAssessment  = lazy(() => import("./DialogAssessmentGuidelines"));

interface INavigationItemProps {
  active?: boolean;
  children?: React.ReactNode;
  complete?: boolean;
  icon: React.ReactNode;
  itemKey: React.Key;
  label: string;
  onClick: () => void;
  optional?: boolean;
}

function belongsToUnity(item: any, unityId: number | string | undefined) {
  const itemUnityId = item?.id_unidade ?? item?.unidade_id;

  if (itemUnityId === undefined || itemUnityId === null) return true;

  return Number(itemUnityId) === Number(unityId);
}

const TITLE_LOWER_CASE_WORDS = new Set([
  "a",
  "as",
  "ao",
  "aos",
  "da",
  "das",
  "de",
  "do",
  "dos",
  "e",
  "em",
  "na",
  "nas",
  "no",
  "nos",
  "o",
  "os",
  "para",
  "por",
]);

// Numeral romano canônico. Uma classe solta como [IVXLCDM]+ casaria
// palavras do português ("civil", "mil", "vil") e as forçaria em caixa alta.
const ROMAN_NUMERAL_PATTERN =
  /^M{0,3}(CM|CD|D?C{0,3})(XC|XL|L?X{0,3})(IX|IV|V?I{0,3})$/;

// Faixas latinas usadas no lugar de \p{L}/\p{N}: o tsconfig do projeto
// tem target es5 e os unicode property escapes exigem es2018 no runtime.
const WORD_PUNCTUATION_PATTERN =
  /^([^0-9A-Za-zÀ-ÖØ-öø-ÿ]*)(.*?)([^0-9A-Za-zÀ-ÖØ-öø-ÿ]*)$/;

/**
 *
 * Separa a pontuação anexa ao token do seu miolo alfanumérico,
 * para que a checagem de caixa ignore caracteres como ":", "." e parênteses
 * @param   {string} word
 * @returns {{ prefix: string, core: string, suffix: string }}
 */
function splitWordPunctuation(word: string) {
  const match = word.match(WORD_PUNCTUATION_PATTERN);

  if (!match) return { prefix: "", core: word, suffix: "" };

  const [, prefix, core, suffix] = match;

  return { prefix, core, suffix };
}

/**
 *
 * Aplica Title Case ao título da unidade preservando numerais romanos
 * e siglas que a API entrega em caixa alta
 * @param   {string | undefined} title
 * @returns {string}
 */
function formatUnityTitle(title: string | undefined) {
  if (!title) return "";

  const trimmedTitle = title.trim();

  if (!trimmedTitle) return "";

  // Títulos integralmente em caixa alta são normalizados: nesse cenário
  // preservar siglas manteria o título inteiro em maiúsculas.
  const shouldKeepAcronyms =
    trimmedTitle !== trimmedTitle.toLocaleUpperCase("pt-BR");

  return trimmedTitle
    .split(/\s+/)
    .map((word, index) => {
      const { prefix, core, suffix } = splitWordPunctuation(word);

      if (!core) return word;

      const lowerCaseCore = core.toLocaleLowerCase("pt-BR");
      const upperCaseCore = core.toLocaleUpperCase("pt-BR");

      // Só preserva o que a API já entregou em caixa alta. Assim "Civil"
      // ou "Mix" em caixa mista seguem pelo Title Case normal.
      const isUpperCaseCore = core === upperCaseCore && core !== lowerCaseCore;

      if (isUpperCaseCore && ROMAN_NUMERAL_PATTERN.test(core)) return word;

      if (isUpperCaseCore && shouldKeepAcronyms && core.length > 1) return word;

      if (index > 0 && TITLE_LOWER_CASE_WORDS.has(lowerCaseCore)) {
        return prefix + lowerCaseCore + suffix;
      }

      const titleCasedCore =
        lowerCaseCore.charAt(0).toLocaleUpperCase("pt-BR") +
        lowerCaseCore.slice(1);

      return prefix + titleCasedCore + suffix;
    })
    .join(" ");
}

/**
 *
 * Exibe menu de navegação lateral do Material Didático
 * @param {SetStateAction}  setOpen
 * @returns {React.JSX}
 */
export default function ListMenuNavigation({ setOpen } : IListMenuNavigationProps) {
  const {
    theme,
    subject,
    unitys,
    unitySelected,
    setUnitySelected,
    videos,
    script_pratical,
    depeeningKnowledge,
    learningResources,
    onHandleUpdateLogs,
    logs,
    insertRouteToUnity,
  } = useDataContext();

  const { setRoutes, totalProgress } = useBookProgress();

  const navigate                                                = useNavigate();
  const location                                                = useLocation();
  const [openAssessmentGuidelines, setOpenAssessmentGuidelines] = useState(false);
  const [expandedUnity, setExpandedUnity]                       = useState<string | false>(false);

  const visibleUnitys = useMemo(() => {
    if (!Array.isArray(unitys)) return [];

    return unitys;
  }, [unitys]);

  const selectedVideos = useMemo(() => {
    if (!Array.isArray(videos)) return [];

    return videos.filter((video: any) => belongsToUnity(video, unitySelected?.id));
  }, [videos, unitySelected?.id]);

  const selectedLearningResources = useMemo(() => {
    if (!Array.isArray(learningResources)) return [];

    return learningResources.filter((item: any) => belongsToUnity(item, unitySelected?.id));
  }, [learningResources, unitySelected?.id]);

  const selectedDeepeningKnowledge = useMemo(() => {
    if (!Array.isArray(depeeningKnowledge)) return [];

    return depeeningKnowledge.filter((item: any) => belongsToUnity(item, unitySelected?.id));
  }, [depeeningKnowledge, unitySelected?.id]);

  const currentLogs = useMemo(() => {
    if (Number(logs?.id_unidade) !== Number(unitySelected?.id)) return undefined;

    return logs;
  }, [logs, unitySelected?.id]);

  useEffect(() => {
    if (unitySelected?.id) {
      setExpandedUnity(getUnityPanelId(unitySelected.id));
    }
  }, [unitySelected?.id]);

  /**
   * Function for verify if the item is active
   */
  function isItemNavigateActive(route: string): boolean {
    return location.pathname === route;
  };

  function isUnitySelected(unity: any) {
    return Number(unity?.id) === Number(unitySelected?.id);
  }

  function getUnityPanelId(unityId: number | string | undefined) {
    return `unity-${unityId}`;
  }

  function isFirstUnity(unity: any, index?: number) {
    if (typeof index === "number") return index === 0;

    return Number(visibleUnitys[0]?.id) === Number(unity?.id);
  }

  function getInitialRouteForUnity(unity: any, index: number): INavigationProps {
    if (isFirstUnity(unity, index)) {
      return {
        url: "/como-estudar",
        pathActived: 0,
        typeLog: "ComoEstudar",
      };
    }

    return {
      url: "/resumo-unidade",
      pathActived: 1,
      typeLog: "ResumoDaUnidade",
    };
  }

  /**
   * Este método remove as credenciais da sessionStorage,
   * removendo assim o usuário conectado
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  function signOut() {
    sessionStorage.removeItem('@md_S');
    sessionStorage.removeItem('@md_AS');

    setTimeout(() => {
        window.location.href = `/${subject?.id}`;
    }, 100);
  }

  /**
   * @description Método para gerenciar navegação e sincronização de logs e progresso
   * @param {string} url
   * @param {number|string} pathActived
   * @param {number|string} subpathActived
   * @param {any} typeLog
   * @param {any} resource
   * @param {any} changed
   * @param {Boolean}  unity
   * @returns
   */
  function navigation ({ url, pathActived, typeLog, subpathActived = 0, resource}: INavigationProps) {
    insertRouteToUnity(unitySelected.id,{url:url,path: pathActived, subpath: subpathActived})
    navigate(url);
    setOpen && setOpen(false);

    onHandleUpdateLogs({typeLog: typeLog, resource: resource});
  }

  function handleUnityChange(unity: any, index: number, isExpanded: boolean) {
    const panelId = getUnityPanelId(unity?.id);

    if (!isExpanded) {
      // Ignora fechamento de outra unidade quando já existe uma nova aberta.
      setExpandedUnity((currentExpanded) => (
        currentExpanded === panelId ? false : currentExpanded
      ));
      return;
    }

    setExpandedUnity(panelId);

    if (!isUnitySelected(unity)) {
      const initialRoute = getInitialRouteForUnity(unity, index);

      setUnitySelected(unity);
      navigate(initialRoute.url);
    }
  }

  function isLogCompleted(typeLog: string) {
    return Boolean(currentLogs?.log_info && (currentLogs?.log_info as any)[typeLog]);
  }

  function renderNavigationItem({
    active = false,
    children,
    complete,
    icon,
    itemKey,
    label,
    onClick,
    optional = false,
  }: INavigationItemProps) {
    const showProgressStatus = typeof complete === "boolean";

    function renderProgressStatusIcon() {
      if (active) {
        return <RadioButtonChecked className="progress_status_icon current" />;
      }

      if (complete) {
        return <CheckCircle className="progress_status_icon complete" />;
      }

      return <RadioButtonUnchecked className="progress_status_icon pending" />;
    }

    return (
      <ListItem
        className={`item_nav ${active ? "active" : ""} ${complete ? "is-complete" : "is-pending"} ${optional ? "optional" : theme}`}
        key={itemKey}
      >
        <ListItemButton onClick={onClick}>
          <ListItemIcon>
            {showProgressStatus && renderProgressStatusIcon()}

            <span className="topic_state_icon">{icon}</span>
          </ListItemIcon>

          {children || <ListItemText primary={label} />}
        </ListItemButton>
      </ListItem>
    );
  }

  /**
   * Envia um novo log ao submeter um novo documento para o Aprofundando o Conhecimento
   */
  useEffect(()=>{
    if(
      logs?.log_info
      && logs?.log_info["AprofundandoNoConhecimento"]
      && logs?.log_info["AprofundandoNoConhecimento"]?.length === Array(depeeningKnowledge).length
    ) {
      /**
       * Realiza a tentativa e em caso de falha exibe o erro no console da página
       * Este comportamento deve ser modificado no futuro, inserindo conexão com uma ferramenta
       * de monitoramento como o Sentry.io
       */
      try {
          axios.post('https://homolog-api-logger.aiamis.com.br/ecossistema/logs',
              {
                  ra: JSON.parse(window.atob(sessionStorage.getItem('@md_S') as string)).ra,
                  module: 'MATERIAL_DIDATICO',
                  evento: {
                      label: 'USUARIO_ACESSA_RECURSO_APROF_CONHECIMENTO',
                      log_info: {
                          ra: JSON.parse(window.atob(sessionStorage.getItem('@md_S') as string)).ra,
                          user_type: JSON.parse(window.atob(sessionStorage.getItem('@md_S') as string)).user.typeUser,
                          info: JSON.parse(window.atob(sessionStorage.getItem('@md_S') as string)).user.info
                      }
                  },
                  metadata: JSON.parse(window.atob(sessionStorage.getItem('@md_S') as string)).metadata,
              }
          );
      } catch(err) {
          console.error(err);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[])

/**
 * Gerar objeto de rotas da unidade que servirá como "gabarito" da navegação
 * Executar sempre no primeiro carregamento, buscando as rotas existentes e as adicionando a um array plano
 */
  useEffect(() => {
    if(unitys && unitySelected && (unitySelected !== undefined || unitySelected !== null) && selectedVideos && selectedLearningResources) {
        var vt: any[]               = [];
        var vp: any[]               = [];
        var rp: any[]               = [];
        var vd: any[]               = [];
        var ra: any[]               = [];

        if(selectedDeepeningKnowledge && selectedDeepeningKnowledge.length > 0) {
          vd.unshift({url: "/aprofundando-conhecimento",pathActived: 7, subpathActived: selectedDeepeningKnowledge.map((item: any) => item.id)});
        }

        if (selectedLearningResources && selectedLearningResources.length > 0) {
            ra.unshift({url: "/recursos-aprendizagem", pathActived: 2, subpathActived: selectedLearningResources.map((item: any) => item.id)})
        }

        if (selectedVideos) {
          vt = selectedVideos.filter((video: any) => {
            if(video.type.id) {
              return video
            }
          }).map((video: any, index: number)=>{
            return {url: "/videoaulas/"+ video.id, pathActived: 3, subpathActived: index}
          });

          vp = selectedVideos.filter((praticVideo: any) => {
            if (praticVideo.type.id === 6) {
              return praticVideo
            }
          }).map((video: any, index: number)=>{
            return {url: "/aula-pratica/"+ video.id, pathActived: 10, subpathActived: index}
          })

          if(vp.length > 0){
            vp.unshift({ url: "/roteiro-pratica", pathActived: 9});
          }
          if(vp.length > 0){
            rp.unshift({ url: "/roteiro-pratica", pathActived: 9});
          }
        }

        if(unitySelected.id !== undefined && unitySelected.titulo_unidade !== "") {
          var unityRoutes = {
            id_unidade: unitySelected.id,
            titulo_unidade: unitySelected.titulo_unidade,
            routes: [
              { url: "/resumo-unidade", pathActived: 1},
              ...ra,
              ...vt,
              { url: "/guia-estudos", pathActived: 4},
              { url: "/partilhando-ideias", pathActived: 5},
              { url: "/contribuindo-documento", pathActived: 6},
              ...vd,
              { url: "/avaliar-unidade", pathActived: 8},
              ...rp,
            ]
          }

          if(unitySelected.id === unitys[0].id)  {
            unityRoutes.routes.unshift({ url: "/como-estudar", pathActived: 0 })
          }
          setRoutes([unityRoutes])
        }
      }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[selectedVideos, unitys, unitySelected, selectedDeepeningKnowledge, selectedLearningResources])

  const categorizedVideos = useMemo(() => {
    const categories: any = {};

    selectedVideos?.forEach((video: any) => {
      if (video?.type?.video_lab_mat_didatico === 1) {
        if (!categories["LABORATÓRIOS"]) {
          categories["LABORATÓRIOS"] = [];
        }
        categories["LABORATÓRIOS"].push(video);
      } else {
        const categoryName = video.type.nome;
        if (!categories[categoryName]) {
          categories[categoryName] = [];
        }
        categories[categoryName].push(video);
      }
    });

    return categories;
  }, [selectedVideos]);

  const learningResourceIds = selectedLearningResources.map((item: any) => item.id);
  const deepeningKnowledgeIds = selectedDeepeningKnowledge.map((item: any) => item.id);

  const finalListing = Object.entries(categorizedVideos).map(([category, videosInCategory]: any) => (
    <React.Fragment key={category}>
      <ListItem>
        <Typography className="title_navigation" variant="subtitle2">
          {category}
        </Typography>
      </ListItem>

      <Divider />

      {videosInCategory?.map((video: any, index: any) => (
        renderNavigationItem({
          active: isItemNavigateActive(`/videoaulas/${video?.id}`),
          complete: Boolean(
            currentLogs?.log_info &&
            Array.isArray(currentLogs?.log_info["Videoaulas"]) &&
            currentLogs?.log_info["Videoaulas"].includes(video?.id)
          ),
          icon: <PlayCircleOutline />,
          itemKey: video?.id,
          label: video?.titulo,
          onClick: () =>
            navigation({
              url: `/videoaulas/${video?.id}`,
              pathActived: 3,
              typeLog: "Videoaulas",
              subpathActived: index,
              resource:[video?.id]
            }),
          children: (
            <div className="video_nav_text">
              <span>{video.type.nome} </span>
              <ListItemText primary={video.titulo} />
            </div>
          )
        })
      ))}

      <Divider />
    </React.Fragment>
  ));

  function renderUnityTopics(unity: any, index: number) {
    if (!isUnitySelected(unity)) {
      return (
        <List className="unity_topics" disablePadding>
          {renderNavigationItem({
            complete: false,
            icon: <ArticleOutlined />,
            itemKey: `initial-${unity?.id}`,
            label: isFirstUnity(unity, index) ? "Como estudar" : "Resumo da Unidade",
            onClick: () => {
              const initialRoute = getInitialRouteForUnity(unity, index);

              setUnitySelected(unity);
              navigate(initialRoute.url);
              setOpen && setOpen(false);
            },
          })}
        </List>
      );
    }

    return (
      <List className="unity_topics" disablePadding>
        {renderNavigationItem({
          icon: (
            <>
              <HelpOutline />
              <BookOutlined />
            </>
          ),
          itemKey: "assessment-guidelines",
          label: "Orientações para Avaliações",
          onClick: () => setOpenAssessmentGuidelines(true),
          optional: true,
        })}

        {isFirstUnity(unity, index) && (
          renderNavigationItem({
            active: isItemNavigateActive("/como-estudar"),
            complete: true,
            icon: <ArticleOutlined />,
            itemKey: "how-to-study",
            label: "Como estudar",
            onClick: () =>
              navigation({
                url: "/como-estudar",
                pathActived: 0,
                typeLog: "ComoEstudar"
              }),
          })
        )}

        {renderNavigationItem({
          active: isItemNavigateActive("/resumo-unidade"),
          complete: isLogCompleted("ResumoDaUnidade"),
          icon: <ArticleOutlined />,
          itemKey: "unit-summary",
          label: "Resumo da Unidade",
          onClick: () =>
            navigation({
              url: "/resumo-unidade",
              pathActived: 1,
              typeLog: "ResumoDaUnidade",
            }),
        })}

        {renderNavigationItem({
          active: isItemNavigateActive("/recursos-aprendizagem"),
          complete: Boolean(
            currentLogs?.log_info &&
            currentLogs?.log_info["RecursosDeAprendizagem"] &&
            currentLogs?.log_info["RecursosDeAprendizagem"]?.length === selectedLearningResources?.length
          ),
          icon: <ArticleOutlined />,
          itemKey: "learning-resources",
          label: "Recursos de aprendizagem",
          onClick: () =>
            navigation({
              url: "/recursos-aprendizagem",
              pathActived: 2,
              typeLog: "RecursosDeAprendizagem",
              subpathActived: learningResourceIds,
              resource: learningResourceIds
            }),
        })}

        <Divider />

        {finalListing}

        <ListItem>
          <Typography className="title_navigation" variant="subtitle2">
            Interagindo
          </Typography>
        </ListItem>

        <Divider />

        {renderNavigationItem({
          active: isItemNavigateActive("/guia-estudos"),
          complete: isLogCompleted("GuiaDeEstudos"),
          icon: <SchoolOutlined />,
          itemKey: "study-guide",
          label: "Guia de estudos",
          onClick: () =>
            navigation({
              url: "/guia-estudos",
              pathActived: 4,
              typeLog: "GuiaDeEstudos",
            }),
        })}

        {renderNavigationItem({
          active: isItemNavigateActive("/partilhando-ideias"),
          complete: isLogCompleted("PartilhandoIdeias"),
          icon: <EmojiObjectsOutlined />,
          itemKey: "sharing-ideas",
          label: "Partilhando ideias",
          onClick: () =>
            navigation({
              url: "/partilhando-ideias",
              pathActived: 5,
              typeLog: "PartilhandoIdeias",
            }),
        })}

        {renderNavigationItem({
          active: isItemNavigateActive("/contribuindo-documento"),
          complete: isLogCompleted("ContribuaComUmDocumento"),
          icon: <CloudUploadOutlined />,
          itemKey: "contributing-document",
          label: "Contribua com um documento",
          onClick: () =>
            navigation({
              url: "/contribuindo-documento",
              pathActived: 6,
              typeLog: "ContribuaComUmDocumento",
            }),
        })}

        {Array.isArray(selectedDeepeningKnowledge) && selectedDeepeningKnowledge?.length > 0 && (
          renderNavigationItem({
            active: isItemNavigateActive("/aprofundando-conhecimento"),
            complete: Boolean(
              currentLogs?.log_info &&
              currentLogs?.log_info["AprofundandoNoConhecimento"] &&
              currentLogs?.log_info["AprofundandoNoConhecimento"]?.length === selectedDeepeningKnowledge?.length
            ),
            icon: <WbIncandescentOutlined />,
            itemKey: "deepening-knowledge",
            label: "Aprofundando o conhecimento",
            onClick: () =>
              navigation({
                url: "/aprofundando-conhecimento",
                pathActived: 7,
                typeLog: "AprofundandoNoConhecimento",
                subpathActived: deepeningKnowledgeIds,
                resource: deepeningKnowledgeIds
              }),
          })
        )}

        {renderNavigationItem({
          active: isItemNavigateActive("/avaliar-unidade"),
          complete: isLogCompleted("AvaliarUnidade"),
          icon: <ThumbUpOutlined />,
          itemKey: "evaluate-unit",
          label: "Avaliar a unidade",
          onClick: () =>
            navigation({
              url: "/avaliar-unidade",
              pathActived: 8,
              typeLog: "AvaliarUnidade",
            }),
        })}

        {script_pratical && script_pratical.guiaPraticoUrl && (
          <>
            <Divider />

            <ListItem>
              <Typography className="title_navigation" variant="subtitle2">
                Orientações para prática
              </Typography>
            </ListItem>

            <Divider />

            {renderNavigationItem({
              active: isItemNavigateActive("/roteiro-pratica"),
              complete: isLogCompleted("RoteiroParaAulaPrática"),
              icon: <BallotOutlined />,
              itemKey: "practice-script",
              label: "Roteiro para aula prática",
              onClick: () =>
                navigation({
                  url: "/roteiro-pratica",
                  pathActived: 9,
                  typeLog: "RoteiroParaAulaPrática",
                }),
            })}
          </>
        )}
      </List>
    );
  }

  return (
    <React.Fragment>
      <List className="drawer_menu">
        <Toolbar style={{ minWidth: "20rem" }} />

        <ListItem>
          <Box className="area_nav_info">
            <Box>
              <Button
                startIcon={<HomeOutlined />}
                sx={{ display: { md: "none" } }}
                variant="outlined"
                onClick={onGoEcossistema}
              >
                Voltar
              </Button>

              <Typography variant="subtitle1">{subject?.nome}</Typography>
            </Box>

            <Box>
              <LinearProgress
                className="progress"
                variant="determinate"
                value={Number(totalProgress)}
                color="success"
              />

              <Typography className="text_progress">
                {totalProgress}% completo
              </Typography>
            </Box>
          </Box>
        </ListItem>

        <Divider />

        <Box className="unity_sidebar">
          {visibleUnitys.length > 0 ? (
            visibleUnitys.map((unity: any, index: number) => {
              const selected = isUnitySelected(unity);
              const panelId = getUnityPanelId(unity?.id);

              return (
                <Accordion
                  className={`unity_accordion ${selected ? `selected ${theme}` : ""}`}
                  disableGutters
                  expanded={expandedUnity === panelId}
                  key={unity?.id}
                  onChange={(_, isExpanded) => handleUnityChange(unity, index, isExpanded)}
                  square
                >
                  <AccordionSummary
                    aria-controls={`${panelId}-content`}
                    className="unity_accordion_summary"
                    expandIcon={<ExpandMore />}
                    id={`${panelId}-header`}
                  >
                    <Box className="unity_header">
                      <span className="unity_index">{index + 1}</span>

                      <Box>
                        <Typography className="unity_label" variant="caption">
                          Unidade {index + 1}
                        </Typography>

                        <Typography className="unity_title" variant="subtitle2">
                          {formatUnityTitle(unity?.titulo_unidade)}
                        </Typography>
                      </Box>
                    </Box>
                  </AccordionSummary>

                  <AccordionDetails className="unity_accordion_details">
                    {renderUnityTopics(unity, index)}
                  </AccordionDetails>
                </Accordion>
              )
            })
          ) : (
            <Box className="navigation_loading">
              <Loading title="Carregando navegação..." />
            </Box>
          )}
        </Box>
      </List>

      <DialogAssessment
        open={openAssessmentGuidelines}
        setOpen={setOpenAssessmentGuidelines}
      />
    </React.Fragment>
  );
}